import type { OrbitalElements } from './types';
import { positionAtMeanAnomaly } from './kepler';
import { orbitEllipse } from './orbit-geometry';

/**
 * Pure equal-time swept-area sector geometry (Story 2.1, FR-9), a `core` leaf
 * (AD-1): no DOM, canvas, framework, network, storage, or wall-clock access, and
 * no AU, day, or degree literal (SI metres and radians only, AD-5). Deterministic
 * given `{ a, e }` and a sector count. It imports ONLY from `./kepler`,
 * `./orbit-geometry`, and `./types` (nothing from `state`/`render`/`ui`).
 *
 * The load-bearing invariant (Kepler's second law made geometric):
 *   Mean anomaly advances UNIFORMLY with time (M = n*t, Story 1.2's motion model),
 *   so equal intervals of TIME are exactly equal intervals of MEAN ANOMALY. Dividing
 *   one period into N equal time intervals therefore means sector boundaries at
 *   mean anomalies M_i = 2*pi*i / N for i = 0 .. N. These are NOT equal-angle
 *   (equal true-anomaly) wedges: equal angle at the focus sweeps UNEQUAL area.
 *
 * Why the areas come out exactly equal (the analytic anchor):
 *   The area swept from periapsis to eccentric anomaly E is
 *     A(E) = (a*b/2) * (E - e*sin E) = (a*b/2) * M,   since M = E - e*sin E,
 *   so swept area is LINEAR in M. Hence the area between two mean anomalies is
 *     sweptArea = 0.5 * a * b * (M2 - M1),   b = a*sqrt(1 - e^2),
 *   and every equal-time sector has EXACTLY area pi*a*b / N, regardless of where it
 *   sits on the orbit (short and fat near periapsis, long and thin near apoapsis).
 *
 * The M -> position map (`positionAtMeanAnomaly`) uses only `{ a, e }`, so sector
 * geometry is independent of the central body's mu, of `state.time`, and of any
 * dragged phase offset: the overlay describes the WHOLE orbit, not the body's
 * instantaneous phase.
 */

/** Default number of equal-time sectors (FR-9). */
export const DEFAULT_SECTOR_COUNT = 6;
/** Minimum permitted sector count (FR-9). */
export const MIN_SECTOR_COUNT = 2;
/** Maximum permitted sector count (FR-9). */
export const MAX_SECTOR_COUNT = 24;

const TWO_PI = 2 * Math.PI;

/** A single equal-time swept-area sector in the focus-centred SI frame. */
export interface Sector {
  /** Zero-based sector index, 0 .. count - 1. */
  readonly index: number;
  /** Mean anomaly at the sector's start boundary, radians. */
  readonly startMeanAnomaly: number;
  /** Mean anomaly at the sector's end boundary, radians. */
  readonly endMeanAnomaly: number;
  /**
   * Closed wedge polygon in focus-centred world metres:
   * [focus (0,0), P(startM), ...arc samples..., P(endM)].
   */
  readonly polygon: readonly { x: number; y: number }[];
  /** Exact analytic swept area of the sector's span, SI m^2 (positive). */
  readonly area: number;
}

/**
 * The equal-time boundary mean anomalies [0, 2*pi/N, 2*(2*pi/N), ..., 2*pi], length
 * N + 1. Equal TIME = equal MEAN ANOMALY, so these are the sector boundaries. The
 * caller passes a validated integer N in [2, 24] (the reducer clamps; the UI never
 * sends otherwise), but this stays defensive: a non-finite or < 1 count yields a
 * single degenerate span [0, 2*pi].
 */
export function sectorBoundaryMeanAnomalies(count: number): number[] {
  const n = Number.isFinite(count) && count >= 1 ? Math.floor(count) : 1;
  const boundaries: number[] = [];
  for (let i = 0; i <= n; i++) {
    boundaries.push((TWO_PI * i) / n);
  }
  return boundaries;
}

/**
 * The EXACT analytic swept-sector area between two mean anomalies in the
 * focus-centred frame: 0.5 * a * b * (endM - startM) with b = a*sqrt(1 - e^2), SI
 * m^2 (positive for endM > startM). Linear in M because the periapsis-to-E swept
 * area is (a*b/2)*(E - e*sin E) = (a*b/2)*M (see module header).
 */
export function sweptArea(
  elements: OrbitalElements,
  startMeanAnomaly: number,
  endMeanAnomaly: number
): number {
  const { semiMajor, semiMinor } = orbitEllipse(elements);
  return 0.5 * semiMajor * semiMinor * (endMeanAnomaly - startMeanAnomaly);
}

/**
 * Assign a fill CLASS (0 | 1 | 2) to sector `index` of a cycle of `count` sectors
 * such that NO two cyclically-adjacent sectors share a class, INCLUDING the
 * wrap-around seam between the last sector (index count - 1) and sector 0.
 *
 * A perfect 2-tone alternation is only possible around an EVEN cycle: for odd
 * count the two ends of the parity chain (index 0 and index count - 1) are both
 * even-parity and would collide across the seam. So:
 *   - EVEN count: two classes, `index % 2` (0 / 1) — identical to the historical
 *     behaviour, no visual change to the common default of 6.
 *   - ODD count: parity (0 / 1) for indices 0 .. count - 2, and a THIRD class (2)
 *     for the LAST sector (index count - 1) so it differs from BOTH its neighbour
 *     (count - 2, class 1) AND sector 0 (class 0).
 *
 * Pure (AD-1): a total function of two integers, no DOM/Date/palette. The renderer
 * owns the class -> tone/alpha mapping. Defensive for count < 2 (single sector):
 * returns 0.
 */
export function sectorFillClass(index: number, count: number): 0 | 1 | 2 {
  if (count < 2) return 0;
  if (count % 2 === 1 && index === count - 1) return 2;
  return index % 2 === 0 ? 0 : 1;
}

/**
 * Build `count` equal-time sectors for the orbit `{ a, e }`. Sector k spans
 * [M_k, M_{k+1}] with the polygon [focus(0,0), P(M_k), P(M_k + dm), ..., P(M_{k+1})],
 * where P(m) = positionAtMeanAnomaly(elements, m) and `dm` steps the arc into
 * `arcSamplesPerSector` sub-segments so the wedge fill hugs the true ellipse (a
 * chord polygon UNDERESTIMATES the arc, worst near periapsis at high e; >= 16
 * samples keeps the relative error small). Each sector's `area` is the EXACT
 * analytic `sweptArea` for its span, not the chord approximation.
 */
export function buildSectors(
  elements: OrbitalElements,
  count: number,
  arcSamplesPerSector = 16
): Sector[] {
  const boundaries = sectorBoundaryMeanAnomalies(count);
  const samples = Number.isFinite(arcSamplesPerSector)
    ? Math.max(1, Math.floor(arcSamplesPerSector))
    : 16;
  const sectors: Sector[] = [];
  for (let k = 0; k < boundaries.length - 1; k++) {
    const startM = boundaries[k];
    const endM = boundaries[k + 1];
    const dm = (endM - startM) / samples;
    const polygon: { x: number; y: number }[] = [{ x: 0, y: 0 }];
    for (let s = 0; s <= samples; s++) {
      polygon.push(positionAtMeanAnomaly(elements, startM + dm * s));
    }
    sectors.push({
      index: k,
      startMeanAnomaly: startM,
      endMeanAnomaly: endM,
      polygon,
      area: sweptArea(elements, startM, endM)
    });
  }
  return sectors;
}
